import React from 'react';
import dayjs from 'dayjs';
import {
  Box,
  Card,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Alert,
  Typography,
  Grid,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssessmentIcon from '@mui/icons-material/Assessment';

import Client from '../Client';
import Config from '../ClientConfig';
import { locationsList } from './simpleBiReportsApi';

class RawMaterialConsumption extends React.Component {
  constructor(props) {
    super(props);

    const today = dayjs().format('YYYY-MM-DD');

    this.state = {
      client: new Client('none'),
      loading: false,

      locations: [],
      products: [],
      productNameMap: {},

      filters: {
        date: today,
        location: '',
        product: '',
      },
      filtersCollapsed: false,

      summaryCollapsed: false,

      daily: [],
      openingStock: 0,
      theoreticalTotal: 0,
      unitLabel: '',

      totalStockInBase: 0,
      totalStockInAdjusted: 0,
      totalStockOutTransfers: 0,
      totalStockOutDisplay: 0,
      totalAdjustments: 0,

      adjustmentsByDate: {},

      closingStockInput: '',

      realConsumption: null,
      varianceQty: null,
      variancePct: null,
      closingTheoretical: null,

      page: 0,
      rowsPerPage: -1,
      tableHeight: 400,

      infoText: '',
      errorText: '',
    };

    this.tableContainerRef = React.createRef();
  }

  componentDidMount() {
    this.fetchLookups();

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.updateTableLayout);
    }

    setTimeout(this.updateTableLayout, 0);
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.updateTableLayout);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevState.filtersCollapsed !== this.state.filtersCollapsed ||
      prevState.summaryCollapsed !== this.state.summaryCollapsed ||
      prevState.daily !== this.state.daily
    ) {
      this.updateTableLayout();
    }
  }

  updateTableLayout = () => {
    if (typeof window === 'undefined') return;

    const container = this.tableContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();

    const paginationHeight = 64;
    const marginBottom = 12;

    const available =
      window.innerHeight - rect.top - paginationHeight - marginBottom;

    const minHeight = 180;
    const height = Math.max(minHeight, available);

    this.setState((prev) =>
      prev.tableHeight !== height ? { tableHeight: height } : null
    );
  };

  escapeCSV(value) {
    const s = value == null ? '' : String(value);

    if (/[",\n]/.test(s)) {
      return '"' + s.replace(/"/g, '""') + '"';
    }

    return s;
  }

  formatQtyDisplay = (val) => {
    const n = Number(val);

    if (!Number.isFinite(n)) return '';

    if (Math.abs(n - Math.trunc(n)) < 1e-9) {
      return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
    }

    return n.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3,
    });
  };

  formatQtyCsv = (val) => {
    const n = Number(val);

    if (!Number.isFinite(n)) return '';

    if (Math.abs(n - Math.trunc(n)) < 1e-9) {
      return String(Math.trunc(n));
    }

    return n.toFixed(3).replace(/\.?0+$/, '');
  };

  fetchLookups() {
    this.setState({ loading: true, errorText: '', infoText: '' });

    try {
      const locationsResp = locationsList(this.state.client) || {};

      const productsResp =
        this.state.client.getData(
          (Config.API_URL_PRODUCT || '') + 'list_products/',
          { status: 2, isPurchased: 1, orderBy_name: true }
        ) || {};

      const locs = Array.isArray(locationsResp.locations)
        ? locationsResp.locations
        : [];

      const locations = locs.sort((a, b) => {
        const an = ((a && (a.name || a.description)) || '').toLowerCase();
        const bn = ((b && (b.name || b.description)) || '').toLowerCase();

        return an.localeCompare(bn);
      });

      const products = Array.isArray(productsResp.products)
        ? productsResp.products
        : [];

      const productNameMap = {};

      for (const p of products) {
        const pid = p?.iref || p?.uid || p?.code || p?.id || p?.product;

        const pname =
          p?.name ||
          p?.productName ||
          p?.description ||
          p?.code ||
          pid;

        if (pid) {
          productNameMap[String(pid)] = String(pname);
        }
      }

      this.setState({
        locations,
        products,
        productNameMap,
        loading: false
      });
    } catch (e) {
      this.setState({
        loading: false,
        errorText: 'Failed to load lookups.',
      });
    }
  }

  buildQuery() {
    const f = this.state.filters || {};
    const date = f.date || dayjs().format('YYYY-MM-DD');

    const q = { asof: date };

    if (f.location) q.location = f.location;
    if (f.product) q.product = f.product;

    return q;
  }

  fetchData = () => {
    const { filters, client } = this.state;

    if (!filters.location || !filters.product) {
      this.setState({
        daily: [],
        openingStock: 0,
        theoreticalTotal: 0,
        totalStockInBase: 0,
        totalStockInAdjusted: 0,
        totalStockOutTransfers: 0,
        totalStockOutDisplay: 0,
        totalAdjustments: 0,
        realConsumption: null,
        varianceQty: null,
        variancePct: null,
        adjustmentsByDate: {},
        page: 0,
        infoText: '',
        errorText: 'Please select both Location and Product.',
      });

      return;
    }

    this.setState({
      loading: true,
      page: 0,
      infoText: '',
      errorText: '',
      daily: [],
      adjustmentsByDate: {},
      closingStockInput: '',
      realConsumption: null,
      varianceQty: null,
      variancePct: null,
    });

    const q = this.buildQuery();

    const baseUrl =
      Config.API_URL_REPORTS_TRANSACTIONS || Config.API_URL_REPORTS || '';

    const url = baseUrl + 'rm_consumption/';

    let resp = {};

    try {
      resp = client.getData(url, q) || {};
    } catch (e) {
      this.setState({ loading: false, errorText: 'Request failed.' });
      return;
    }

    if (resp && resp.error) {
      this.setState({
        loading: false,
        errorText: resp.detail || resp.error || 'Request failed.',
      });

      return;
    }

    const daily = Array.isArray(resp.daily) ? resp.daily : [];

    const openingStock = Number(
      resp.opening_stock != null ? resp.opening_stock : 0
    );

    const theoreticalTotal = Number(
      resp.theoretical_total != null ? resp.theoretical_total : 0
    );

    const productInfo = (resp.filters && resp.filters.product) || {};
    const unitLabel = productInfo.uom || productInfo.unit || '';

    let totalStockInBase = 0;
    let totalStockOutTransfers = 0;

    daily.forEach((d) => {
      const inBase =
        Number(d.stock_in_receipts || 0) +
        Number(d.stock_in_transfers || 0);

      const outTransfers = Number(d.stock_out_transfers || 0);

      totalStockInBase += inBase;
      totalStockOutTransfers += outTransfers;
    });

    const totalAdjustments = 0;
    const totalStockInAdjusted = totalStockInBase + totalAdjustments;
    const totalStockOutDisplay = theoreticalTotal + totalStockOutTransfers;

    this.setState(
      {
        loading: false,
        daily,
        openingStock,
        theoreticalTotal,
        unitLabel: unitLabel || '',
        totalStockInBase,
        totalStockInAdjusted,
        totalStockOutTransfers,
        totalStockOutDisplay,
        totalAdjustments,
        infoText: daily.length
          ? `Period from ${resp.filters?.start || ''} to ${
              resp.filters?.end || ''
            }`
          : '',
      },
      () => {
        this.recomputeDerivedMetrics();
        this.updateTableLayout();
      }
    );
  };

  recomputeDerivedMetrics = () => {
    const {
      openingStock,
      theoreticalTotal,
      totalStockInBase,
      totalStockOutTransfers,
      closingStockInput,
      adjustmentsByDate,
    } = this.state;

    let totalAdjustments = 0;

    if (adjustmentsByDate) {
      Object.values(adjustmentsByDate).forEach((v) => {
        const n = Number(v);

        if (Number.isFinite(n)) {
          totalAdjustments += n;
        }
      });
    }

    const stockInAdjusted = totalStockInBase + totalAdjustments;
    const totalStockOutDisplay = theoreticalTotal + totalStockOutTransfers;

    const closingStr = (closingStockInput || '').trim();

    const baseState = {
      totalAdjustments,
      totalStockInAdjusted: stockInAdjusted,
      totalStockOutDisplay,
      realConsumption: null,
      closingTheoretical: null,
      varianceQty: null,
      variancePct: null,
    };

    if (closingStr === '') {
      this.setState(baseState);
      return;
    }

    const closing = Number(closingStr.replace(',', '.'));

    if (!Number.isFinite(closing)) {
      this.setState(baseState);
      return;
    }

    const realConsumption =
      Number(openingStock || 0) +
      Number(stockInAdjusted || 0) -
      Number(totalStockOutTransfers || 0) -
      closing;

    const closingTheoretical =
      Number(openingStock || 0) +
      Number(stockInAdjusted || 0) -
      Number(totalStockOutTransfers || 0) -
      Number(theoreticalTotal || 0);

    const varianceQty = closing - closingTheoretical;

    const variancePct =
      closingTheoretical && Math.abs(closingTheoretical) > 1e-9
        ? (varianceQty / closingTheoretical) * 100.0
        : null;

    this.setState({
      totalAdjustments,
      totalStockInAdjusted: stockInAdjusted,
      totalStockOutDisplay,
      realConsumption,
      closingTheoretical,
      varianceQty,
      variancePct,
    });
  };

  onChangeClosing = (raw) => {
    raw = raw || '';
    raw = raw.replace(',', '.');

    if (raw === '') {
      this.setState({ closingStockInput: '' }, this.recomputeDerivedMetrics);
      return;
    }

    if (raw === '-' || raw === '+') {
      this.setState({ closingStockInput: raw }, this.recomputeDerivedMetrics);
      return;
    }

    const decimalPattern = /^[-+]?\d*(\.\d{0,3})?$/;

    if (!decimalPattern.test(raw)) {
      return;
    }

    this.setState({ closingStockInput: raw }, this.recomputeDerivedMetrics);
  };

  onChangeAdjustment = (dateStr, valueStr) => {
    let raw = valueStr || '';
    raw = raw.replace(',', '.');

    this.setState(
      (prev) => {
        const adjustmentsByDate = { ...(prev.adjustmentsByDate || {}) };

        if (raw === '') {
          delete adjustmentsByDate[dateStr];
          return { adjustmentsByDate };
        }

        if (raw === '-' || raw === '+') {
          adjustmentsByDate[dateStr] = raw;
          return { adjustmentsByDate };
        }

        const decimalPattern = /^[-+]?\d*(\.\d{0,3})?$/;

        if (!decimalPattern.test(raw)) {
          return { adjustmentsByDate };
        }

        if (raw.endsWith('.')) {
          adjustmentsByDate[dateStr] = raw;
          return { adjustmentsByDate };
        }

        const n = Number(raw);

        if (Number.isFinite(n)) {
          adjustmentsByDate[dateStr] = n;
        } else {
          adjustmentsByDate[dateStr] = raw;
        }

        return { adjustmentsByDate };
      },
      this.recomputeDerivedMetrics
    );
  };

  exportCSV = () => {
    const st = this.state;
    const filters = st.filters || {};
    const locations = st.locations || [];
    const productNameMap = st.productNameMap || {};
    const daily = st.daily || [];
    const adjustmentsByDate = st.adjustmentsByDate || {};

    const asof = dayjs(
      filters.date || dayjs().format('YYYY-MM-DD')
    ).format('YYYY-MM-DD');

    const locId = filters.location;

    const locObj = (locations || []).find(
      (l) => String(l.iref || l.uid || l.id) === String(locId)
    );

    const locationLabel = locId
      ? (locObj && (locObj.name || locObj.description)) || String(locId)
      : 'Unknown';

    const prodId = filters.product;

    const productLabel = prodId
      ? productNameMap[String(prodId)] || String(prodId)
      : 'Unknown';

    const generatedAt = dayjs().format('YYYY-MM-DD HH:mm:ss');

    const summaryRows = [
      ['Summary'],
      ['Date', asof],
      ['Location', locationLabel],
      ['Product (RM)', productLabel],
      ['Opening stock', this.formatQtyCsv(st.openingStock)],
      ['Stock IN (base)', this.formatQtyCsv(st.totalStockInBase)],
      ['Adjustments', this.formatQtyCsv(st.totalAdjustments)],
      ['Stock IN (adjusted)', this.formatQtyCsv(st.totalStockInAdjusted)],
      ['Theoretical consumption (Script Out)', this.formatQtyCsv(st.theoreticalTotal)],
      ['Stock OUT transfers', this.formatQtyCsv(st.totalStockOutTransfers)],
      [
        'Total OUT theoretical (Script + transfers)',
        this.formatQtyCsv(st.totalStockOutDisplay),
      ],
      ['Closing stock (Final, input)', this.formatQtyCsv(st.closingStockInput)],
      ['Real consumption (Real Out)', this.formatQtyCsv(st.realConsumption)],
      ['Theoretical closing stock', this.formatQtyCsv(st.closingTheoretical)],
      ['Closing variance qty (Final - Script)', this.formatQtyCsv(st.varianceQty)],
      [
        'Closing variance %',
        st.variancePct != null ? this.formatQtyCsv(st.variancePct) : '',
      ],
      ['Generated at', generatedAt],
      [''],
    ].map((row) => row.map((v) => this.escapeCSV(v)).join(','));

    const header = ['Date', 'Stock IN', 'Stock OUT', 'Adjustment'].join(',');

    const lines = daily.map((d) => {
      const dateStr = d.date || '';

      const baseIn =
        Number(d.stock_in_receipts || 0) +
        Number(d.stock_in_transfers || 0);

      const adj = Number(adjustmentsByDate[dateStr] || 0);
      const stockInDisplay = baseIn + adj;

      const stockOutDisplay =
        Number(d.stock_out_sales_theoretical || 0) +
        Number(d.stock_out_transfers || 0);

      return [
        this.escapeCSV(dateStr),
        this.formatQtyCsv(stockInDisplay),
        this.formatQtyCsv(stockOutDisplay),
        this.formatQtyCsv(adj),
      ].join(',');
    });

    const csv = [...summaryRows, header, ...lines].join('\r\n');

    const dateSafe = (asof || 'date').replace(/[^0-9-]/g, '');

    const locSafe = (locationLabel || 'location').replace(
      /[^a-z0-9-_]+/gi,
      '_'
    );

    const prodSafe = (productLabel || 'product').replace(
      /[^a-z0-9-_]+/gi,
      '_'
    );

    const fname =
      'RawMaterialConsumption_' +
      dateSafe +
      '_' +
      locSafe +
      '_' +
      prodSafe +
      '.csv';

    const blob = new Blob(['\uFEFF' + csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = fname;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  toggleFiltersCollapsed = () => {
    this.setState(
      (prev) => ({ filtersCollapsed: !prev.filtersCollapsed }),
      this.updateTableLayout
    );
  };

  toggleSummaryCollapsed = () => {
    this.setState(
      (prev) => ({ summaryCollapsed: !prev.summaryCollapsed }),
      this.updateTableLayout
    );
  };

  renderFilters() {
    const st = this.state;
    const { filtersCollapsed } = st;
    const filters = st.filters || {};
    const locations = st.locations || [];
    const products = st.products || [];
    const productNameMap = st.productNameMap || {};

    const productOptions = products.map((p) => {
      const value = String(
        p.iref || p.uid || p.code || p.id || p.product || ''
      );

      const label =
        p.name || p.productName || p.description || p.code || value;

      return { value, label };
    });

    const selectedProdVal =
      filters && filters.product != null ? String(filters.product) : '';

    const productValue =
      productOptions.find((o) => o.value === selectedProdVal) || null;

    const selectedProductLabel =
      productNameMap[String(filters.product)] ||
      String(filters.product || '');

    return (
      <Box
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          px: {
            xs: 1,
            sm: 1.5
          },
          py: {
            xs: 0.75,
            sm: 1
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: filtersCollapsed ? 0 : 0.75
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FilterAltIcon fontSize="small" />
            <Typography
              sx={{
                fontSize: {
                  xs: "13px",
                  sm: "14px"
                },
                fontWeight: 700
              }}
            >
              Filters
            </Typography>
          </Box>

          <Button
            size="small"
            onClick={this.toggleFiltersCollapsed}
            startIcon={
              filtersCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />
            }
            sx={{
              minHeight: 30,
              fontSize: {
                xs: "12px",
                sm: "13px"
              }
            }}
          >
            {filtersCollapsed ? 'Show' : 'Hide'}
          </Button>
        </Box>

        {!filtersCollapsed && (
          <Stack spacing={1}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={5}>
                <TextField
                  select
                  fullWidth
                  label="Location"
                  value={filters.location}
                  onChange={(e) =>
                    this.setState((prev) => ({
                      filters: {
                        ...(prev.filters || {}),
                        location: e.target.value,
                      },
                    }))
                  }
                  size="small"
                  margin="dense"
                  sx={this.filterSx()}
                >
                  <MenuItem value="">Select...</MenuItem>
                  {locations.map((loc) => (
                    <MenuItem
                      key={String(loc.iref || loc.uid || loc.id)}
                      value={String(loc.iref || loc.uid || loc.id)}
                    >
                      {loc.name ||
                        loc.description ||
                        String(loc.iref || loc.uid || loc.id)}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={7}>
                <Autocomplete
                  size="small"
                  value={productValue}
                  options={productOptions}
                  isOptionEqualToValue={(o, v) =>
                    ((o && o.value) || '') === ((v && v.value) || '')
                  }
                  onChange={(_e, opt) => {
                    const newVal = opt ? opt.value : '';

                    this.setState((prev) => ({
                      filters: {
                        ...(prev.filters || {}),
                        product: newVal,
                      },
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Product"
                      margin="dense"
                      sx={{
                        ...this.filterSx(),
                        width: '100%',
                        '& .MuiInputBase-input': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textAlign: 'left',
                          fontSize: {
                            xs: "16px",
                            sm: "14px"
                          }
                        },
                      }}
                      InputProps={{
                        ...(params.InputProps || {}),
                      }}
                      inputProps={{
                        ...(params.inputProps || {}),
                        title: selectedProductLabel,
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexWrap: 'nowrap' }}
            >
              <TextField
                label="Date"
                type="date"
                value={filters.date}
                onChange={(e) =>
                  this.setState((prev) => ({
                    filters: {
                      ...(prev.filters || {}),
                      date: e.target.value,
                    },
                  }))
                }
                size="small"
                margin="dense"
                sx={{
                  ...this.filterSx(),
                  width: {
                    xs: 150,
                    sm: 160
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />

              <Box sx={{ flexGrow: 1 }} />

              <Button
                variant="contained"
                size="small"
                onClick={this.fetchData}
                disabled={this.state.loading}
                sx={{
                  whiteSpace: 'nowrap',
                  minHeight: 34,
                  fontSize: {
                    xs: "12px",
                    sm: "13px"
                  }
                }}
              >
                Apply
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={this.exportCSV}
                disabled={!this.state.daily.length}
                sx={{
                  whiteSpace: 'nowrap',
                  minHeight: 34,
                  display: {
                    xs: "none",
                    sm: "inline-flex"
                  },
                  fontSize: {
                    xs: "12px",
                    sm: "13px"
                  }
                }}
              >
                CSV
              </Button>
            </Stack>
          </Stack>
        )}

        {!!this.state.errorText && (
          <Alert severity="error" sx={{ mt: 0.75 }}>
            {this.state.errorText}
          </Alert>
        )}
      </Box>
    );
  }

  renderSummary() {
    const {
      openingStock,
      theoreticalTotal,
      totalStockOutTransfers,
      totalStockInAdjusted,
      closingStockInput,
      realConsumption,
      varianceQty,
      unitLabel,
      summaryCollapsed,
    } = this.state;

    const unitSuffix = unitLabel ? ` ${unitLabel}` : '';

    const formatWithUnit = (val) =>
      val != null && val !== ''
        ? `${this.formatQtyDisplay(val)}${unitSuffix}`
        : '';

    const kpiRow = (label, { value, isInput = false }) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: {
            xs: 28,
            sm: 28
          },
          width: '100%',
          gap: 0.75,
        }}
      >
        <Typography
          sx={{
            flex: 1,
            textAlign: 'right',
            pr: 0.75,
            fontWeight: 700,
            color: 'primary.main',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.2,
            fontSize: {
              xs: "12px",
              sm: "13px",
              md: "14px"
            },
          }}
        >
          {label}
        </Typography>

        {isInput ? (
          <TextField
            value={closingStockInput}
            onChange={(e) => this.onChangeClosing(e.target.value)}
            size="small"
            margin="dense"
            placeholder="0"
            sx={{
              width: {
                xs: 92,
                sm: 120,
                md: 150,
                lg: 180
              },
              '& .MuiInputBase-root': {
                height: 28,
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiInputBase-input': {
                textAlign: 'right',
                fontSize: {
                  xs: "16px",
                  sm: "14px"
                },
                padding: '0 4px',
              },
            }}
          />
        ) : (
          <Box
            sx={{
              width: {
                xs: 92,
                sm: 120,
                md: 150,
                lg: 180
              },
              minHeight: 28,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: {
                  xs: "16px",
                  sm: "14px",
                  md: "15px"
                },
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              {value || '-'}
            </Typography>
          </Box>
        )}
      </Box>
    );

    return (
      <Box
        sx={{
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          px: {
            xs: 1,
            sm: 1.5
          },
          py: {
            xs: 0.75,
            sm: 1
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: summaryCollapsed ? 0 : 0.75,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <AssessmentIcon fontSize="small" />
            <Typography
              sx={{
                fontSize: {
                  xs: "13px",
                  sm: "14px"
                },
                fontWeight: 700
              }}
            >
              Summary
            </Typography>
          </Box>

          <Button
            size="small"
            onClick={this.toggleSummaryCollapsed}
            startIcon={
              summaryCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />
            }
            sx={{
              minHeight: 30,
              fontSize: {
                xs: "12px",
                sm: "13px"
              }
            }}
          >
            {summaryCollapsed ? 'Show' : 'Hide'}
          </Button>
        </Box>

        {summaryCollapsed ? null : (
          <Grid container spacing={0.75}>
            <Grid item xs={6}>
              {kpiRow('Initial', {
                value: formatWithUnit(openingStock),
              })}
            </Grid>

            <Grid item xs={6}>
              {kpiRow('In', {
                value: formatWithUnit(totalStockInAdjusted),
              })}
            </Grid>

            <Grid item xs={6}>
              {kpiRow('Out Script', {
                value: formatWithUnit(theoreticalTotal + totalStockOutTransfers),
              })}
            </Grid>

            <Grid item xs={6}>
              {kpiRow('Out Real', {
                value:
                  realConsumption != null
                    ? formatWithUnit(realConsumption)
                    : '',
              })}
            </Grid>

            <Grid item xs={6}>
              {kpiRow('Final', {
                value: formatWithUnit(closingStockInput),
                isInput: true,
              })}
            </Grid>

            <Grid item xs={6}>
              {kpiRow('Var', {
                value:
                  varianceQty != null
                    ? formatWithUnit(varianceQty)
                    : '',
              })}
            </Grid>
          </Grid>
        )}
      </Box>
    );
  }

  renderTable() {
    const {
      loading,
      daily,
      adjustmentsByDate,
      tableHeight,
    } = this.state;

    const paged = daily;

    return (
      <Box
        sx={{
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column"
        }}
      >
        <TableContainer
          ref={this.tableContainerRef}
          sx={{
            height: tableHeight,
            overflowX: 'auto',
            '& .MuiTableCell-root': {
              px: {
                xs: 0.7,
                sm: 1
              },
              py: {
                xs: 0.65,
                sm: 0.6
              },
              fontSize: {
                xs: "13.5px",
                sm: "14px",
                md: "15px"
              },
            },
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{ tableLayout: 'fixed' }}
          >
            <colgroup>
              <col style={{ width: '30%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '20%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>

            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, backgroundColor: "#f6f4ef" }}>
                  Date
                </TableCell>

                <TableCell sx={{ fontWeight: 700, backgroundColor: "#f6f4ef" }} align="right">
                  In
                </TableCell>

                <TableCell sx={{ fontWeight: 700, backgroundColor: "#f6f4ef" }} align="right">
                  Out
                </TableCell>

                <TableCell sx={{ fontWeight: 700, backgroundColor: "#f6f4ef" }} align="right">
                  Adj +/-
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {paged.map((row, idx) => {
                const dateStr = row.date || '';

                const baseIn =
                  Number(row.stock_in_receipts || 0) +
                  Number(row.stock_in_transfers || 0);

                const adj = Number(adjustmentsByDate[dateStr] || 0);
                const stockInDisplay = baseIn + adj;

                const stockOutDisplay =
                  Number(row.stock_out_sales_theoretical || 0) +
                  Number(row.stock_out_transfers || 0);

                return (
                  <TableRow key={dateStr || String(idx)} hover>
                    <TableCell>{dateStr}</TableCell>

                    <TableCell align="right">
                      {this.formatQtyDisplay(stockInDisplay)}
                    </TableCell>

                    <TableCell align="right">
                      {this.formatQtyDisplay(stockOutDisplay)}
                    </TableCell>

                    <TableCell
                      align="right"
                      sx={{ overflow: 'hidden' }}
                    >
                      <Box
                        sx={{
                          width: {
                            xs: 68,
                            sm: 80,
                            md: 100,
                            lg: 120
                          },
                          ml: 'auto',
                        }}
                      >
                        <TextField
                          value={
                            adjustmentsByDate[dateStr] !== undefined
                              ? String(adjustmentsByDate[dateStr])
                              : ''
                          }
                          onChange={(e) =>
                            this.onChangeAdjustment(dateStr, e.target.value)
                          }
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiInputBase-root': {
                              height: 28,
                            },
                            '& input': {
                              py: 0.3,
                              fontSize: {
                                xs: "16px",
                                sm: "14px"
                              },
                              padding: '0 4px',
                              textAlign: 'right',
                            },
                          }}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={daily.length}
          page={0}
          onPageChange={() => {}}
          rowsPerPage={-1}
          rowsPerPageOptions={[]}
          sx={{
            borderTop: "1px solid rgba(0,0,0,0.08)",
            '& .MuiTablePagination-toolbar': {
              px: {
                xs: 1,
                sm: 2
              },
              minHeight: 42,
            },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
              display: 'none',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: {
                xs: "13px",
                sm: "13px"
              },
            },
          }}
          labelDisplayedRows={({ count }) =>
            count === -1 ? '0 of 0' : `1–${count} of ${count}`
          }
        />

        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              bgcolor: 'rgba(255,255,255,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>
    );
  }

  filterSx() {
    return {
      '& .MuiInputBase-input': {
        fontSize: {
          xs: "16px",
          sm: "14px"
        }
      },
      '& .MuiInputLabel-root': {
        fontSize: {
          xs: "13px",
          sm: "13px"
        }
      }
    };
  }

  render() {
    return (
      <Card
        sx={{
          borderRadius: "14px",
          overflow: "hidden",
          maxWidth: "100%",
          height: {
            xs: "calc(100vh - 92px)",
            md: "calc(100vh - 104px)"
          },
          minHeight: {
            xs: 500,
            sm: 540
          },
          display: "flex",
          flexDirection: "column"
        }}
      >
        {this.renderFilters()}
        {this.renderSummary()}
        {this.renderTable()}
      </Card>
    );
  }
}

export default RawMaterialConsumption;
